import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table } from "antd";
import ListTopSearch from "../../Global Functions/listTopSearch";
import { getReelRankingList, updateReelRanking } from "../../Redux/Actions/reelRankingActions";
import { columnsReelRanking } from "../../Global Functions/columnsAntd";
import toast from "react-hot-toast";
import DateRangePopup from "../../Utils/dateFilterPopup";
import RankingEditModal from "./updateReelRankingPopup";
import MainLayout from "../../Layout/mainLayout";
import CityBusinessFilterDropdown from "./businessTypePopup";

const ListProjectResaleRating = () => {
    const dispatch = useDispatch();

    const [searchQuery, setSearchQuery] = useState("");
    const [cityId, setCityId] = useState("6808dd3def8fb1411ef46efa");
    const [businessType, setBusinessType] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
    const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [isRankingModalVisible, setIsRankingModalVisible] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [projectType, setProjectType] = useState("");

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    const { reelRankings, loading } = useSelector((state) => state.reels);
    const { citiesList } = useSelector((state) => state.city || {});
    const cityData = citiesList?.data ?? [];

    useEffect(() => {
        if (cityData.length && !cityId) {
            const mohali = cityData.find(
                city => city.city_name.toLowerCase() === "mohali"
            );
            if (mohali) {
                setCityId(mohali._id);
            }
        }
    }, [cityData, cityId]);

    const fetchDetails = useCallback(
        async (page, limit, showLoader = true) => {
            const rankingData = {
                page,
                limit,
                is_deleted: false,
                filter: debouncedSearch,
                ...(businessType && { business_type: businessType }),
                ...(projectType && { asset_type: projectType }),
                ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
                ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
                ...(cityId && { city: cityId }),
            };

            await dispatch(getReelRankingList(rankingData, showLoader));
        },
        [dispatch, debouncedSearch, cityId, dateRange, businessType, projectType]
    );

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    useEffect(() => {
        fetchDetails(pagination.current, pagination.pageSize, false);
    }, [
        pagination.current,
        pagination.pageSize,
        debouncedSearch,
        cityId,
        dateRange,
        businessType,
        projectType,
        fetchDetails,
    ]);

    useEffect(() => {
        if (typeof reelRankings?.total === "number") {
            setPagination((prev) => ({ ...prev, total: reelRankings.total }));
        }
    }, [reelRankings]);

    const handleDateRangeApply = (start, end) => {
        if (start === null && end === null) {
            setDateRange({ start: null, end: null });
        } else {
            setDateRange({ start, end });
        }
        setIsDatePopupVisible(false);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const handleTableChange = (pager) => {
        setPagination((prev) => ({
            ...prev,
            current: pager.current,
            pageSize: pager.pageSize,
        }));
    };

    const handleOpenRankingModal = (record) => {
        setSelectedProject(record);
        setIsRankingModalVisible(true);
    };

    const handleCloseRankingModal = () => {
        setIsRankingModalVisible(false);
        setSelectedProject(null);
    };

    const handleSaveRanking = async (payload) => {
        try {
            await dispatch(updateReelRanking(payload));
            toast.success("Reel ranking updated successfully!");
            handleCloseRankingModal();
            fetchDetails(pagination.current, pagination.pageSize, false);
        } catch (error) {
            console.error("Failed to update ranking", error);
            toast.error("Failed to update reel ranking");
        }
    };

    const handleCityChange = (e) => {
        setCityId(e.target.value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleBusinessTypeChange = (e) => {
        setBusinessType(e.target.value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleProjectTypeChange = (e) => {
        setProjectType(e.target.value);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    return (
        <MainLayout>
            <div className="bookings_table">
                <div className="table_component_all">
                    <div style={{ gridColumn: "span 5", height: "auto" }}>
                        <ListTopSearch
                            h3heading="Reels List"
                            setState={setSearchQuery}
                            onCalendarClick={() => setIsDatePopupVisible(true)}
                        />
                    </div>

                    <div style={{
                        position: "static",
                        marginTop: "-72px",
                        marginLeft: "84%",
                        marginBottom: "24px"
                    }}>
                        <CityBusinessFilterDropdown
                            cityValue={cityId}
                            businessValue={businessType}
                            projectValue={projectType}
                            onCityChange={handleCityChange}
                            onBusinessChange={handleBusinessTypeChange}
                            onProjectChange={handleProjectTypeChange}
                        />
                    </div>

                    <div className="custom_table">
                        <div style={{ width: "90%" }}>
                            <Table
                                dataSource={(Array.isArray(reelRankings?.data) && reelRankings.data) || []}
                                virtual
                                scroll={{ x: 1700, y: 500 }}
                                pagination={pagination}
                                onChange={handleTableChange}
                                loading={loading}
                                columns={columnsReelRanking(pagination.current, pagination.pageSize, handleOpenRankingModal, projectType)}
                                rowClassName={() => "text-center"}
                                rowKey={(record) => record._id}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <DateRangePopup
                visible={isDatePopupVisible}
                initialStart={dateRange.start}
                initialEnd={dateRange.end}
                onCancel={() => setIsDatePopupVisible(false)}
                onOk={handleDateRangeApply}
            />

            <RankingEditModal
                visible={isRankingModalVisible}
                onClose={handleCloseRankingModal}
                projectData={selectedProject}
                onSave={handleSaveRanking}
            />
        </MainLayout>
    );
};

export default ListProjectResaleRating;